/**
 * bookingDetailModel — pure display + gating helpers for the booking detail
 * view (BON-145). Kept framework-free so the branching logic (action gating,
 * status vocabulary, provenance, history, money) is unit-testable in isolation
 * and reusable by every subcomponent.
 */
import type { BookingSource, BookingStatus } from '../../api/bookingsV2';

/**
 * Action gating is FE-derived from status: Rebook + Refund are meaningful ONLY
 * on a live CONFIRMED booking. Every terminal state (rebooked, refunded,
 * cancelled) disables them — each has already given up its seats, its money, or
 * both. (Contract "consumption rules".)
 */
export function canActOnBooking(status: BookingStatus): boolean {
  return status === 'CONFIRMED';
}

/**
 * Refunding needs more than a live booking: it returns *cash*, so there has to
 * be cash recorded against it. Comps are CONFIRMED with nothing paid, and
 * imported rows FareHarbor gave no `amount_paid` for carry `null` — both are
 * refusable, and the API refuses them with `NOTHING_PAID`. Gating on the same
 * fact here turns a 409 the CSR only meets after typing a reason into a
 * disabled control that says why up front.
 *
 * Deliberately net cash, never the receipt total: the receipt is what was
 * contracted, this is what is actually there to give back.
 */
export function hasRefundableAmount(amountPaidCents: number | null): boolean {
  return amountPaidCents !== null && amountPaidCents > 0;
}

/** A refund to gift passes returns seats, not cash, so while passes are on a
 *  booking paid in full by gift card or passes ($0 cash) is refundable too.
 *  Only an import with no payment record (null) is not. */
function hasSomethingToRefund(
  amountPaidCents: number | null,
  giftPassesEnabled: boolean | null,
): boolean {
  if (giftPassesEnabled) return amountPaidCents !== null;
  return hasRefundableAmount(amountPaidCents);
}

export interface RefundAffordance {
  enabled: boolean;
  /** Why it is disabled. Only meaningful when `enabled` is false. */
  hint: string;
}

/**
 * The single answer to "can this user refund this booking, and if not, why".
 *
 * Both action surfaces (the desktop rail card and the mobile bar) render the
 * same decision, so computing it twice invites them to drift — and the reason
 * ORDER is the part that matters: a comp cannot be refunded by anyone, so
 * blaming permissions there would send an operator to ask for a grant that
 * changes nothing. Most specific true reason wins.
 */
export function refundAffordance(
  status: BookingStatus,
  canRefund: boolean,
  amountPaidCents: number | null,
): RefundAffordance {
  if (!canActOnBooking(status)) {
    return { enabled: false, hint: 'Only available on a confirmed booking' };
  }
  if (!canRefund) {
    return { enabled: false, hint: 'You do not have permission to issue refunds' };
  }
  if (!hasRefundableAmount(amountPaidCents)) {
    return { enabled: false, hint: 'This booking has no recorded payment to refund' };
  }
  return { enabled: true, hint: '' };
}

export interface RebookAffordance {
  enabled: boolean;
  /** Why it is disabled. Only meaningful when `enabled` is false. */
  hint: string;
}

/**
 * The single answer to "can this user move this booking, and if not, why".
 *
 * Both action surfaces render this, and the reason ORDER is the part that
 * matters — same rule as {@link refundAffordance}, most specific true reason
 * wins. A cancelled booking that is also past its cutoff must not blame the
 * cutoff: asking a manager to override would change nothing, and the CSR would
 * have spent someone else's time finding that out.
 *
 * `cutoffPassed` is the SERVER's verdict, never derived here. Knowing when
 * 10:00 falls at a venue needs that venue's timezone, which this payload does
 * not carry, and a second implementation of the rule would drift from the one
 * that actually refuses the write.
 *
 * Unlike a refund there is no money precondition: a rebook moves whatever cash
 * the booking holds, including none. A comp rebooks fine.
 */
export function rebookAffordance(
  status: BookingStatus,
  canRebook: boolean,
  cutoffPassed: boolean,
  canOverrideCutoff: boolean,
): RebookAffordance {
  if (!canActOnBooking(status)) {
    return { enabled: false, hint: 'Only available on a confirmed booking' };
  }
  if (!canRebook) {
    return { enabled: false, hint: 'You do not have permission to move bookings' };
  }
  if (cutoffPassed && !canOverrideCutoff) {
    return {
      enabled: false,
      hint: 'Past the 10 AM cutoff on the day of the workshop — ask a manager',
    };
  }
  return { enabled: true, hint: '' };
}

/** Neither retire path is allowed once the workshop has ended. Guards the empty
 *  string the backend sends for a missing workshop — it parses to NaN, which
 *  would otherwise read as "not ended". */
export function workshopHasEnded(endsAt: string, now: number = Date.now()): boolean {
  const ends = Date.parse(endsAt);
  return Number.isFinite(ends) && ends <= now;
}

export interface RetireAffordance {
  enabled: boolean;
  /** Why it is disabled. Only meaningful when `enabled` is false. */
  hint: string;
  /** Which job the one control does for this booking. */
  kind: 'refund' | 'cancel';
}

/**
 * Can this booking be retired, what would that do, and if not, why. One control
 * for both because they are mutually exclusive — the booking decides, not the
 * user.
 *
 * Reason order mirrors the server's refusal ladder: a hint naming a different
 * reason than the endpoint would teaches the wrong rule. Ended-workshop sits
 * ahead of permission because no grant lifts it.
 */
export function retireAffordance(
  status: BookingStatus,
  isComp: boolean,
  amountPaidCents: number | null,
  endsAt: string,
  canRetire: boolean,
  giftPassesEnabled: boolean | null = false,
): RetireAffordance {
  const kind: 'refund' | 'cancel' = isComp ? 'cancel' : 'refund';
  const verb = isComp ? 'cancel' : 'refund';

  if (!canActOnBooking(status)) {
    return { enabled: false, kind, hint: 'Only available on a confirmed booking' };
  }
  if (workshopHasEnded(endsAt)) {
    return { enabled: false, kind, hint: 'This workshop has already taken place' };
  }
  if (!canRetire) {
    return { enabled: false, kind, hint: `You do not have permission to ${verb} bookings` };
  }
  if (isComp && hasRefundableAmount(amountPaidCents)) {
    return {
      enabled: false,
      kind,
      hint: 'Marked as a comp but has a payment recorded — refund it instead',
    };
  }
  if (!isComp && !hasSomethingToRefund(amountPaidCents, giftPassesEnabled)) {
    return { enabled: false, kind, hint: 'This booking has no recorded payment to refund' };
  }
  return { enabled: true, kind, hint: '' };
}

export interface ResendConfirmationAffordance {
  enabled: boolean;
  /** Why it is disabled. Only meaningful when `enabled` is false. */
  hint: string;
}

/**
 * The single answer to "can this user resend this booking's confirmation, and
 * if not, why" (BON-630) — same disabled-CTA pattern and reason ORDER as
 * {@link rebookAffordance} / {@link retireAffordance}: a booking that is not
 * CONFIRMED cannot be resent by anyone, so blaming permission there would send
 * an operator asking for a grant that changes nothing.
 */
export function resendConfirmationAffordance(
  status: BookingStatus,
  canResendConfirmation: boolean,
): ResendConfirmationAffordance {
  if (!canActOnBooking(status)) {
    return { enabled: false, hint: 'Only available on a confirmed booking' };
  }
  if (!canResendConfirmation) {
    return { enabled: false, hint: 'You do not have permission to resend confirmations' };
  }
  return { enabled: true, hint: '' };
}

export type StatusTone = 'confirmed' | 'pending' | 'superseded' | 'refunded' | 'canceled';

export interface StatusDisplay {
  label: string;
  tone: StatusTone;
  /** Tailwind pill classes (badge bg + text), light + dark. */
  badge: string;
  /** The status dot colour. */
  dot: string;
}

const STATUS: Record<BookingStatus, StatusDisplay> = {
  CONFIRMED: {
    label: 'Confirmed',
    tone: 'confirmed',
    badge: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    dot: 'bg-green-500',
  },
  REBOOKED: {
    // "Rebooked", not "Superseded" — this page used to disagree with the list
    // and the pill, which both already said Rebooked (BON-146).
    label: 'Rebooked',
    tone: 'superseded',
    badge: 'bg-stone-100 dark:bg-stone-700/60 text-stone-600 dark:text-stone-300',
    dot: 'bg-stone-400',
  },
  /** Deploy-window alias for REBOOKED — see the BookingStatus union. */
  SUPERSEDED: {
    label: 'Rebooked',
    tone: 'superseded',
    badge: 'bg-stone-100 dark:bg-stone-700/60 text-stone-600 dark:text-stone-300',
    dot: 'bg-stone-400',
  },
  REFUNDED: {
    label: 'Refunded',
    tone: 'refunded',
    badge: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
    dot: 'bg-red-500',
  },
  CANCELLED: {
    label: 'Canceled',
    tone: 'canceled',
    badge: 'bg-stone-100 dark:bg-stone-700/60 text-stone-600 dark:text-stone-300',
    dot: 'bg-stone-400',
  },
};

/** Neutral pill for a status outside the known vocabulary. The backend types
 *  `status` as a bare string and passes it verbatim (and a chain neighbour can
 *  carry a migrated/unknown status), so an out-of-vocabulary value must degrade
 *  to a readable pill rather than dereference `undefined` and white-screen. */
const NEUTRAL_STATUS: StatusDisplay = {
  label: 'Unknown',
  tone: 'superseded',
  badge: 'bg-stone-100 dark:bg-stone-700/60 text-stone-600 dark:text-stone-300',
  dot: 'bg-stone-400',
};

export function statusDisplay(status: string): StatusDisplay {
  return STATUS[status as BookingStatus] ?? NEUTRAL_STATUS;
}

/** Provenance shown in the "Source" slot. */
export function sourceLabel(source: BookingSource): string {
  return source === 'fareharbor' ? 'Migrated' : 'Online booking';
}

export interface HistoryLabel {
  count: string;
  note: string;
}

/**
 * Customer history from `priorBookingsCount` (the customer's OTHER bookings).
 * `> 0` → "Returning customer"; `0` → "New customer".
 */
export function historyLabel(priorBookingsCount: number): HistoryLabel {
  if (priorBookingsCount > 0) {
    const noun = priorBookingsCount === 1 ? 'booking' : 'bookings';
    return { count: `${priorBookingsCount} prior ${noun}`, note: 'Returning customer' };
  }
  return { count: 'No prior bookings', note: 'New customer' };
}

/** Integer cents → a `$X.XX` USD string. */
export function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export function wasServiceFeeCharged(feesCents: number | null): feesCents is number {
  return feesCents !== null && feesCents > 0;
}

/**
 * Refund copy that has to agree between the pre-submit drawer and the receipt
 * that replaces it, so it lives here rather than in either one.
 */
export function seatsNote(ticketsCount: number): string {
  const noun = ticketsCount === 1 ? 'seat' : 'seats';
  return `Full booking only — ${ticketsCount} ${noun} return to the workshop automatically.`;
}

/** Best available name for the customer, falling back through email to a
 *  neutral phrase — never renders an empty string into a sentence. */
export function customerDisplayName(booking: {
  customer: { firstName: string | null; lastName: string | null; email: string | null };
}): string {
  return (
    [booking.customer.firstName, booking.customer.lastName].filter(Boolean).join(' ').trim() ||
    booking.customer.email ||
    'the customer'
  );
}
